import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../api";
import { buildUserFromToken, hasPermission } from "../utils/auth";

export default function ContractChatPage({ resourcePath = "contracts", label = "Contract" }) {
  const { id } = useParams();
  const [viewerRole, setViewerRole] = useState("UNKNOWN");
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState([]);
  const [chats, setChats] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [asking, setAsking] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [contract, setContract] = useState(null);
  const [chatError, setChatError] = useState("");
  const [contractError, setContractError] = useState("");
  const [accessStatus, setAccessStatus] = useState("LOADING");
  const [decisionReason, setDecisionReason] = useState("");
  const [requestReason, setRequestReason] = useState("");
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [chatModel, setChatModel] = useState("gpt-4.1-mini");
  const [isRecording, setIsRecording] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const [voiceLang, setVoiceLang] = useState(
    () => (typeof navigator !== "undefined" && navigator.language) || "en-US"
  );
  const [canChat, setCanChat] = useState(true);
  const typingIntervalRef = useRef(null);
  const askAbortRef = useRef(null);
  const askCancelledRef = useRef(false);
  const currentChatIdRef = useRef(null);
  const recognitionRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const pollTimerRef = useRef(null);
  const chatLocked = contract && contract.status !== "READY";
  const labelLower = String(label || "Contract").toLowerCase();

  const renderStatusChip = (status) => {
    const base =
      "inline-flex mt-1 px-3 py-1 rounded-full text-xs font-medium border";
    if (status === "READY")
      return (
        <span className={`${base} bg-green-50 text-green-700 border-green-200`}>
          READY
        </span>
      );
    if (status === "PROCESSING")
      return (
        <span className={`${base} bg-amber-50 text-amber-700 border-amber-200`}>
          PROCESSING
        </span>
      );
    if (status === "FAILED")
      return (
        <span className={`${base} bg-red-50 text-red-700 border-red-200`}>
          FAILED
        </span>
      );
    return (
      <span className={`${base} bg-slate-50 text-slate-600 border-slate-200`}>
        {status || "UPLOADED"}
      </span>
    );
  };

  const titleFromMessages = (currentTitle, msgs = []) => {
    if (currentTitle && currentTitle !== "New Chat") return currentTitle;
    const firstUser = msgs.find((m) => m.role === "user");
    if (!firstUser?.content) return "New Chat";
    const clean = firstUser.content.trim().replace(/\s+/g, " ");
    return clean.length > 40 ? `${clean.slice(0, 40)}…` : clean;
  };

  const updateChatMessages = (chatId, updater) => {
    setChats((prevChats) => {
      let updatedMessages = null;
      const nextChats = prevChats.map((chat) => {
        if (chat.id !== chatId) return chat;
        const baseMessages = chat.messages || [];
        updatedMessages =
          typeof updater === "function"
            ? updater(baseMessages)
            : updater || [];
        return {
          ...chat,
          messages: updatedMessages,
          title: titleFromMessages(chat.title, updatedMessages),
        };
      });

      if (
        updatedMessages !== null &&
        (chatId === activeChatId || !activeChatId)
      ) {
        setMessages(updatedMessages);
      }

      return nextChats;
    });
  };

  const startNewChat = () => {
    return createChatOnServer();
  };

  const selectChat = (chatId) => {
    const chat = chats.find((c) => c.id === chatId);
    setActiveChatId(chatId);
    setMessages(chat?.messages || []);
    setChatError("");
  };

  const loadAccessStatus = async () => {
    try {
      const res = await api.get(`/${resourcePath}/${id}/access-status`);
      const normalized = (res.data.status || "NOT_REQUESTED").toUpperCase();
      setAccessStatus(normalized);
      setDecisionReason(res.data.decision_reason || "");
    } catch (err) {
      console.error("Access status error:", err);
      setAccessStatus("NOT_REQUESTED");
      setDecisionReason("");
    }
  };

  const requestAccess = async () => {
    try {
      setRequestSubmitting(true);
      const res = await api.post(`/${resourcePath}/${id}/request-access`, {
        reason: requestReason,
      });
      setAccessStatus((res.data.status || "PENDING").toUpperCase());
      setDecisionReason("");
    } catch (err) {
      console.error("Request access error:", err);
      setChatError(
        err.response?.data?.error || "Failed to submit access request."
      );
    } finally {
      setRequestSubmitting(false);
    }
  };

  const fetchContract = async () => {
    try {
      const res = await api.get(`/${resourcePath}/${id}`);
      setContract(res.data);
      if (res.data.status !== "READY") {
        setChatError(
          `${label} is not ready yet. Process the PDF before chatting.`
        );
        setChats([]);
        setMessages([]);
        setActiveChatId(null);
      } else {
        setChatError("");
      }
      setContractError("");
    } catch (err) {
      console.error("Failed to load contract:", err);
      if (err.response?.status === 403) {
        const status = err.response?.data?.access_status || "NOT_REQUESTED";
        setAccessStatus(status);
        setDecisionReason(err.response?.data?.decision_reason || "");
        setContractError(`Access required for this ${labelLower}.`);
      } else {
        setContractError("Unable to load contract details. Please try again.");
      }
    }
  };

  const loadHistory = async () => {
    if (accessStatus !== "APPROVED") return;
    setLoadingHistory(true);
    try {
      const res = await api.get(`/chat/${id}/history`);
      const chatList = Array.isArray(res.data) ? res.data : [];
      setChats(chatList);
      setActiveChatId(chatList[0]?.id || null);
      setMessages(chatList[0]?.messages || []);
    } catch (err) {
      console.error("Failed to load chat history", err);
      setChatError(
        err.response?.data?.error || "Unable to load chat history"
      );
    } finally {
      setLoadingHistory(false);
    }
  };

  const runSearch = async (e) => {
    e.preventDefault();
    if (accessStatus !== "APPROVED") {
      setChatError("Access required before searching.");
      return;
    }
    if (!searchQuery.trim()) return;
    setSearchLoading(true);
    setSearchError("");
    try {
      const res = await api.get(`/${resourcePath}/${id}/semantic-search`, {
        params: { q: searchQuery },
      });
      setSearchResults(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Semantic search error:", err);
      setSearchError(err.response?.data?.error || "Search failed.");
    } finally {
      setSearchLoading(false);
    }
  };

  const createChatOnServer = async () => {
    if (accessStatus !== "APPROVED") {
      setChatError("Access required before chatting.");
      return null;
    }
    try {
      const res = await api.post(`/chat/${id}/history`);
      const chat = res.data;
      setChats((prev) => [chat, ...prev]);
      setActiveChatId(chat.id);
      setMessages([]);
      setChatError("");
      setQuestion("");
      return chat.id;
    } catch (err) {
      console.error("Failed to create chat session", err);
      setChatError(
        err.response?.data?.error || "Unable to start a new chat"
      );
      return null;
    }
  };

  const deleteChatOnServer = async (chatId) => {
    try {
      await api.delete(`/chat/${id}/history/${chatId}`);
      setChats((prev) => {
        const next = prev.filter((c) => c.id !== chatId);
        if (chatId === activeChatId) {
          const fallback = next[0];
          setActiveChatId(fallback?.id || null);
          setMessages(fallback?.messages || []);
        }
        return next;
      });
    } catch (err) {
      console.error("Failed to delete chat session", err);
      setChatError(
        err.response?.data?.error || "Unable to delete chat history"
      );
    }
  };

  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setViewerRole(parsed.role || "UNKNOWN");
        setCanChat(hasPermission(parsed, "chat:use"));
      } catch (_) {
        /* ignore */
      }
    }
    const token = localStorage.getItem("token");
    const user = token ? buildUserFromToken(token) : null;
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
      setViewerRole(user.role || "UNKNOWN");
      setCanChat(hasPermission(user, "chat:use"));
    } else {
      setViewerRole("UNKNOWN");
      setCanChat(false);
    }
    loadAccessStatus();
    fetchContract();
  }, [id]);

  useEffect(() => {
    if (viewerRole === "EMPLOYEE") {
      setChats([]);
      setMessages([]);
      setActiveChatId(null);
      setChatError("");
      return;
    }
    if (accessStatus === "APPROVED") {
      loadHistory();
      if (!contract) {
        fetchContract();
      }
    }
  }, [accessStatus, viewerRole]);

  useEffect(() => {
    if (contract && contract.status === "READY") {
      loadHistory();
    }
  }, [contract, id]);

  // Poll contract status until READY to avoid manual refresh
  useEffect(() => {
    if (!contract || contract.status === "READY") return undefined;
    let attempts = 0;
    const maxAttempts = 120; // ~6 minutes at 3s interval

    const poll = async () => {
      attempts += 1;
      try {
        const res = await api.get(`/contracts/${id}`);
        const fresh = res.data;
        setContract(fresh);
        if (fresh.status === "READY") {
          setContractError("");
          setChatError("");
          if (accessStatus === "APPROVED") {
            loadHistory();
          }
          if (pollTimerRef.current) clearInterval(pollTimerRef.current);
          return;
        }
      } catch (err) {
        // ignore and retry until max attempts
      }
      if (attempts >= maxAttempts && pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
      }
    };

    pollTimerRef.current = setInterval(poll, 3000);
    poll(); // immediate check

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
      }
    };
  }, [contract, id, accessStatus]);

  const animateAssistantResponse = (text, chatId) =>
    new Promise((resolve) => {
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
        typingIntervalRef.current = null;
      }

      updateChatMessages(chatId, (prev) => [
        ...prev,
        { role: "assistant", content: "" },
      ]);

      let index = 0;
      const safeText = text && text.length ? text : "No response provided.";
      typingIntervalRef.current = setInterval(() => {
        if (askCancelledRef.current) {
          clearInterval(typingIntervalRef.current);
          typingIntervalRef.current = null;
          resolve();
          return;
        }
        index += 1;
        updateChatMessages(chatId, (prev) => {
          const next = [...prev];
          const lastIdx = next.length - 1;
          const last = next[lastIdx];
          if (last?.role === "assistant") {
            next[lastIdx] = {
              ...last,
              content: safeText.slice(0, index),
            };
          }
          return next;
        });

        if (index >= safeText.length) {
          clearInterval(typingIntervalRef.current);
          typingIntervalRef.current = null;
          resolve();
        }
      }, 20);
    });

  const askQuestion = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;
    if (!contract || contract.status !== "READY") {
      setChatError("This contract is not ready. Process the PDF first.");
      return;
    }

    let targetChatId = activeChatId;
    if (!targetChatId) {
      targetChatId = await startNewChat();
      if (!targetChatId) return;
    }

    const userMessage = {
      role: "user",
      content: question,
    };

    updateChatMessages(targetChatId, (prev) => [
      ...prev,
      userMessage,
    ]);
    setQuestion("");
    setAsking(true);
    setChatError("");
    askCancelledRef.current = false;
    currentChatIdRef.current = targetChatId;

    if (askAbortRef.current) {
      askAbortRef.current.abort();
      askAbortRef.current = null;
    }
    const controller = new AbortController();
    askAbortRef.current = controller;

    try {
      const res = await api.post(`/chat/${id}`, {
        question: userMessage.content,
        chatId: targetChatId,
        model: chatModel,
      }, { signal: controller.signal });

      if (!askCancelledRef.current) {
        await animateAssistantResponse(
          res.data?.answer || "",
          targetChatId
        );
      }
      if (res.data?.messages) {
        setChats((prev) =>
          prev.map((c) =>
            c.id === targetChatId
              ? {
                  ...c,
                  title: res.data.title || c.title,
                  messages: res.data.messages,
                }
              : c
          )
        );
        setMessages(res.data.messages);
      }
    } catch (err) {
      if (err.name === "CanceledError" || err.name === "AbortError") {
        setChatError("Request cancelled.");
      } else {
        console.error("AI chat error:", err);
        setChatError(
          err.response?.data?.error || "Failed to get AI response"
        );
      }
    } finally {
      setAsking(false);
      askAbortRef.current = null;
      currentChatIdRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setVoiceSupported(false);
      return undefined;
    }
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = voiceLang || "en-US";

    const resetSilenceTimer = () => {
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = setTimeout(() => {
        if (recognitionRef.current) {
          recognitionRef.current.stop();
        }
      }, 2000);
    };

    recognition.onstart = () => {
      setIsRecording(true);
      resetSilenceTimer();
    };
    recognition.onend = () => {
      setIsRecording(false);
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }
    };
    recognition.onerror = (event) => {
      setIsRecording(false);
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }
      setChatError(
        event.error === "not-allowed"
          ? "Microphone access denied."
          : "Voice typing error. Please try again."
      );
    };
    recognition.onresult = (event) => {
      let finalText = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (result.isFinal) {
          finalText += result[0].transcript;
        }
      }
      const clean = finalText.trim();
      if (clean) {
        setQuestion((prev) => {
          const base = prev?.trim() ? `${prev.trim()} ` : "";
          return `${base}${clean}`.trim();
        });
      }
      resetSilenceTimer();
    };

    recognitionRef.current = recognition;
    setVoiceSupported(true);
    resetSilenceTimer();

    return () => {
      recognition.stop();
      recognitionRef.current = null;
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }
      setIsRecording(false);
    };
  }, [voiceLang]);

  const startVoiceInput = () => {
    if (!voiceSupported || !recognitionRef.current || asking || chatLocked) {
      return;
    }
    try {
      setChatError("");
      recognitionRef.current.start();
    } catch (err) {
      console.error("Voice start error:", err);
    }
  };

  const stopVoiceInput = () => {
    if (recognitionRef.current && isRecording) {
      recognitionRef.current.stop();
    }
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
    }
  };

  const cancelInFlight = () => {
    askCancelledRef.current = true;
    if (askAbortRef.current) {
      askAbortRef.current.abort();
      askAbortRef.current = null;
    }
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
      typingIntervalRef.current = null;
    }
    if (currentChatIdRef.current) {
      updateChatMessages(currentChatIdRef.current, (prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last?.role === "assistant" && !last.content) {
          next.pop();
        }
        return next;
      });
    }
    setAsking(false);
    setChatError("Request cancelled.");
  };

  if (!canChat) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 px-4 py-3">
          You do not have permission to use AI chat.
        </div>
      </div>
    );
  }

  if (accessStatus === "LOADING") {
    return (
      <div className="max-w-3xl mx-auto mt-6">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
          <p className="text-sm text-slate-600">Checking access…</p>
        </div>
      </div>
    );
  }

  if (accessStatus !== "APPROVED") {
    return (
      <div className="max-w-3xl mx-auto space-y-4 mt-6">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 space-y-4">
          <h1 className="text-2xl font-bold text-navy">Access Required</h1>
          <p className="text-sm text-slate-600">
            You need CEO approval to view or chat about this contract.
          </p>
          <div>
            <label className="block text-sm font-medium text-slate-800 mb-1">
              Reason (optional)
            </label>
            <textarea
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/70 focus:border-gold/70"
              rows={3}
              value={requestReason}
              onChange={(e) => setRequestReason(e.target.value)}
              placeholder="Explain why you need access"
            />
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={requestAccess}
              disabled={requestSubmitting}
              className="px-4 py-2 rounded-lg bg-navy text-white text-sm font-semibold shadow-sm hover:bg-navy/90 disabled:opacity-60"
            >
              {requestSubmitting ? "Requesting…" : "Request Access"}
            </button>
            <span className="text-xs text-slate-500">
              Current status: {accessStatus === "PENDING" ? "Pending CEO approval" : accessStatus}
              {accessStatus === "REJECTED" && decisionReason
                ? ` • Reason: ${decisionReason}`
                : ""}
            </span>
          </div>
          {chatError && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {chatError}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-navy">
          AI Legal Assistant
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Ask questions and analyze this contract using artificial
          intelligence.
        </p>
      </div>

      {contractError && (
        <div className="border border-red-200 bg-red-50 text-red-700 text-sm rounded-lg px-4 py-3">
          {contractError}
        </div>
      )}

      {/* Contract Info Card */}
      {contract && (
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <p className="text-xs text-slate-500">Title</p>
          <p className="font-semibold text-slate-900">
            {contract.title}
          </p>
        </div>
        <div>
          <p className="text-xs text-slate-500">Type</p>
          <p className="font-semibold text-slate-900">
            {contract.contract_type_name || "-"}
          </p>
        </div>
        <div>
          <p className="text-xs text-slate-500">Status</p>
          {renderStatusChip(contract.status)}
        </div>
        <div className="md:col-span-3">
          <label className="block text-xs text-slate-500 mb-1">
            Reasoning model
          </label>
          <select
            className="w-full md:w-72 rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gold/70 focus:border-gold/70"
            value={chatModel}
            onChange={(e) => setChatModel(e.target.value)}
            disabled={chatLocked}
          >
            <option value="gpt-4.1-mini">gpt-4.1-mini (default)</option>
            <option value="gpt-4o">gpt-4o</option>
            <option value="gpt-4.1">gpt-4.1</option>
          </select>
        </div>
      </div>
      )}

      {/* Chat Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
        {/* Chat History */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm flex flex-col h-[70vh]">
          <div className="flex items-center justify-between gap-2 p-4 border-b border-slate-200">
            <div>
              <p className="text-sm font-semibold text-slate-900">
                Chat History
              </p>
              <p className="text-xs text-slate-500">
                Stored per contract
              </p>
            </div>
            <button
              type="button"
              onClick={startNewChat}
              disabled={chatLocked || loadingHistory}
              className="inline-flex items-center text-xs font-semibold px-3 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              + New Chat
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {loadingHistory && (
              <div className="text-xs text-slate-500 px-3">
                Loading chat history…
              </div>
            )}
            {chats.length === 0 && (
              <div className="text-xs text-slate-500 px-3">
                No chats yet. Start a new one.
              </div>
            )}
            {chats.map((chat) => {
              const lastUser = (chat.messages || [])
                .slice()
                .reverse()
                .find((m) => m.role === "user");

              return (
                <div
                  key={chat.id}
                  className={`w-full flex items-start gap-2 px-3 py-2 rounded-lg border transition shadow-sm ${
                    chat.id === activeChatId
                      ? "bg-navy/5 border-navy text-navy"
                      : "bg-white border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => selectChat(chat.id)}
                    className="flex-1 text-left min-w-0"
                    disabled={chatLocked}
                  >
                    <div className="text-sm font-semibold truncate">
                      {chat.title || "New Chat"}
                    </div>
                    <div className="text-xs text-slate-500 leading-snug overflow-hidden text-ellipsis whitespace-nowrap">
                      {lastUser?.content
                        ? lastUser.content
                        : "No messages yet"}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (
                        window.confirm(
                          "Delete this chat history for this contract?"
                        )
                      ) {
                        deleteChatOnServer(chat.id);
                      }
                    }}
                    className="text-xs text-red-600 hover:text-red-800"
                    title="Delete chat"
                    disabled={chatLocked}
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Chat Container */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm flex flex-col h-[70vh]">
          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {chatLocked && (
              <div className="border border-amber-200 bg-amber-50 text-amber-800 text-sm rounded-lg px-4 py-3">
                This contract is not ready yet. Please wait until processing
                completes before starting an AI chat or search.
              </div>
            )}
            {messages.length === 0 && (
              <div className="text-center text-slate-500 text-sm mt-10">
                <div className="text-center text-xs font-semibold text-slate-600 mb-1">
                  Suggested questions:
                </div>
                <div className="text-center flex flex-wrap gap-2">
                  {[
                    "Summarize the key terms of this contract.",
                    "What are the payment obligations and amounts?",
                    "What are the termination and notice period rules?",
                    "Are there any penalties or risk clauses I should know?",
                  ].map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => setQuestion(q)}
                      className="text-xs px-3 py-1 rounded-full border border-slate-300 text-slate-700 hover:bg-slate-50"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, idx) => (
              <div
                key={idx}
                className={`flex ${
                  m.role === "user" ? "justify-end" : "justify-start"
                }`}
              >
                <div
                  className={`max-w-[80%] rounded-xl px-4 py-3 text-sm leading-relaxed shadow-sm whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-navy text-white"
                      : "bg-slate-100 text-slate-900"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}

            {asking && (
              <div className="flex justify-start">
                <div className="bg-slate-100 px-4 py-3 rounded-xl text-sm text-slate-600 animate-pulse">
                  XpressPaper AI is thinking…
                </div>
              </div>
            )}
          </div>

          {/* Error */}
          {chatError && (
            <div className="px-4 py-2 border-t border-red-200 bg-red-50 text-red-700 text-xs">
              {chatError}
            </div>
          )}

        {/* Input Bar */}
        <form
          onSubmit={askQuestion}
          className="border-t border-slate-200 p-4 flex items-start gap-3"
        >
          <textarea
            rows={2}
            placeholder="Ask a question about this contract..."
            className="flex-1 resize-none rounded-lg border border-slate-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/70 focus:border-gold/70"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            disabled={asking || chatLocked}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                askQuestion(e);
              }
            }}
          />

          <div className="flex flex-col gap-2 items-stretch w-36">
            <button
              type="button"
              onClick={isRecording ? stopVoiceInput : startVoiceInput}
              disabled={!voiceSupported || asking || chatLocked}
              className={`inline-flex items-center justify-center px-3 py-2 rounded-lg text-xs font-semibold shadow-sm border ${
                isRecording
                  ? "border-red-200 bg-red-50 text-red-700"
                  : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
              } disabled:opacity-60 disabled:cursor-not-allowed`}
            >
              {isRecording ? "Stop Voice" : "Voice Type"}
            </button>

            <select
              className="w-full rounded-lg border border-slate-300 px-2 py-2 text-[12px] bg-white focus:outline-none focus:ring-2 focus:ring-gold/70 focus:border-gold/70"
              value={voiceLang}
              onChange={(e) => setVoiceLang(e.target.value)}
              disabled={isRecording || asking || chatLocked}
            >
              <option value="en-US">English (US)</option>
              <option value="en-GB">English (UK)</option>
              <option value="de-DE">Deutsch</option>
              <option value="fr-FR">Français</option>
              <option value="es-ES">Español</option>
              <option value="it-IT">Italiano</option>
            </select>

            {asking ? (
              <button
                type="button"
                onClick={cancelInFlight}
                className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-red-100 text-red-700 text-sm font-semibold shadow-sm hover:bg-red-200 disabled:opacity-60"
              >
                Stop
              </button>
            ) : (
              <button
                type="submit"
                disabled={chatLocked}
                className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-navy text-white text-sm font-semibold shadow-sm hover:bg-navy/90 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Ask
              </button>
            )}

            <p className="text-[11px] leading-tight text-slate-500 text-center">
              {voiceSupported
                ? "Use voice for quick, accurate dictation."
                : "Voice typing needs a supported browser."}
            </p>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
}
